import React, { useContext, useEffect, useState, useRef } from "react";
import Markdown from "react-markdown";
import { logError } from "core/utils/logger";
import { styled } from "styled-components";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { Highlight, themes } from "prism-react-renderer";
import { IconPhotoX, IconAlertTriangle, IconEdit } from "@tabler/icons-react";
import { getWorkbookfromJson } from "core/hooks/jsonToCsv";
import { read, utils } from "xlsx";
import {
  IconFile,
  IconFileZip,
  IconFileText,
  IconFileCode,
  IconFileTypePdf,
  IconFileWord,
  IconFileTypeXls,
  IconFileTypePpt,
  IconDownload,
} from "@tabler/icons-react";
import { usePreview } from "modules/chat/store/previewContext";
import { useAppTheme } from "core/contexts/theme/theme";
import EditIcon from "assets/EditIcon";
import DownloadIcon from "assets/DownloadIcon";
import CheckIcon from "assets/CheckIcon";
import CopyIcon from "assets/CopyIcon";
import { Toast as showToast } from "core/components/global/toast/toast";
import { AuthContext } from "core/contexts/auth/auth";
import { getSignedUrl } from "modules/chat/services/chatService";

type MessageContent =
  | string
  | { text: string }
  | { type: "file"; name: string; url: string; fileType: string };

const MarkdownContainer = styled("div")`
  .content-transition {
    transition: max-height 0.3s ease-in-out;
    overflow: hidden;
  }
  
  .show-more-button {
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    width: fit-content;
    padding: 4px;
    color: ${({ theme }) => theme.colors.textColor};
    font-size: 12px;
    font-weight: 400;
    background: none;
    border: none;
    cursor: pointer;
    transition: opacity 0.2s ease;
    opacity: 0.8;
    
    &:hover {
      opacity: 1;
    }
  }
  .markdown pre {
    overflow-x: auto;
    white-space: pre-wrap;
  }

  // The theme cannot affect this page
  p,
  li,
  span,
  strong,
  answer,
  a,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    color: ${({ theme }) => theme.colors.textColor};
  }

  legend {
    font-size: ${({ theme }) => theme.font.large};
  }

  pre {
    span {
      color: inherit;
    }
  }

  .markdown {
    p {
      margin: 0.25rem 0;
      color: ${({ theme }) => theme.colors.textColor};
    }

    ol,
    ul,
    menu {
      list-style: revert;
      margin: revert;
      padding: revert;
    }

    p strong {
      line-height: 1.8rem;
    }

    li {
      margin: 0.75rem 0;
    }

    h2 {
      font-size: 1.2rem !important;
      font-weight: 600 !important;
    }

    a {
      color: #3b82f6; // Link color
      text-decoration: underline;
      transition: all 0.2s ease;
      position: relative;

      &:hover {
        color: #60a5fa;
        text-decoration: none;
      }

      &:focus {
        outline: 2px solid #60a5fa;
        outline-offset: 2px;
      }
    }
  }

  .prism-code {
    padding: 0.75rem;
  }

  .markdown > pre {
    margin: 0.5rem 0;
    overflow-x: auto;
    border-radius: 0.375rem;
    background-color: rgb(39, 40, 34);
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
      Liberation Mono, Courier New, monospace;
    font-size: 0.75rem;
    line-height: 1rem;
    --tw-text-opacity: 1;
    color: rgb(255 255 255 / var(--tw-text-opacity, 1));
  }

  .markdown > pre {
    box-shadow: 1.5px 3px 3px hsl(0deg 0% 0% / 0.45);
    border-radius: 0.375rem;
  }

  .markdown {
    .code-block-container {
      border-radius: 0.375rem;
      overflow: hidden;

      span {
        color: rgb(255 255 255 / var(--tw-text-opacity, 1));
      }

      .code-block-header {
        border-top-left-radius: 0.375rem;
        border-top-right-radius: 0.375rem;
        
        svg {
          height: 18px;
        }
      }
    }
  }

  .copy-btn {
    position: absolute;
    top: 5px;
    right: 5px;
    background-color: #007bff;
    color: white;
    border: none;
    border-radius: 5px;
    padding: 0.5rem;
    cursor: pointer;
    font-size: 0.75rem;
  }

  .copy-feedback {
    position: absolute;
    top: 5px;
    right: 70px;
    font-size: 0.75rem;
    color: #28a745;
  }

  table {
    border-collapse: collapse;
    width: 100%;
    margin: 1rem 0;
  }

  th,
  td {
    border: 1px solid #ddd;
    padding: 8px;
    text-align: left;
  }

  th {
    background-color: #333;
    color: white;
  }

  tr:nth-child(even) {
    background-color: #2d2d2d;
    color: #ffffff;
  }

  th strong {
    color: ${({ theme }) => theme.name === 'light-theme' ? theme.colors.textColorOpposite : theme.colors.textColor};
  }

  tr:nth-child(even) strong {
    color: ${({ theme }) => theme.name === 'light-theme' ? theme.colors.textColorOpposite : theme.colors.textColor};
  }
`;

// Utility function to get file extension from language
const getFileExtension = (language) => {
  switch (language) {
    case "javascript":
      return "js";
    case "typescript":
      return "ts";
    case "python":
      return "py";
    case "java":
      return "java";
    case "ruby":
      return "rb";
    case "php":
      return "php";
    case "html":
      return "html";
    case "css":
      return "css";
    case "json":
      return "json";
    case "xml":
      return "xml";
    case "bash":
    case "sh":
      return "sh";
    case "c":
      return "c";
    case "cpp":
      return "cpp";
    case "go":
      return "go";
    case "rust":
      return "rs";
    default:
      return "txt";
  }
};

const CopyButtonCodeBlock = ({ value, language, showEditButton = true }) => {
  const [copyStatus, setCopyStatus] = useState("idle"); // idle, copying, copied, error
  const fileExtension = getFileExtension(language);
  const { setPreviewContent } = usePreview();
  const { amsUser } = useContext(AuthContext);
  const showEditButtonProp = showEditButton;

  const copyToClipboard = () => {
    setCopyStatus("copying");
    navigator.clipboard
      .writeText(value)
      .then(() => {
        setCopyStatus("copied");
        setTimeout(() => setCopyStatus("idle"), 2000);
      })
      .catch(() => {
        setCopyStatus("error");
        setTimeout(() => setCopyStatus("idle"), 2000);
      });
  };

  const downloadCode = () => {
    const element = document.createElement("a");
    const file = new Blob([value], { type: "text/plain" });
    element.href = URL.createObjectURL(file);
    element.download = `code.${fileExtension}`;
    document.body.appendChild(element); // Required for this to work in FireFox
    element.click();
  };

  const editCode = async () => {
    try {
      const file = new Blob([value], { type: fileExtension });
      const url = URL.createObjectURL(file);
      const filecontent = {
        name: `code.${fileExtension}`,
        url: url,
        fileType: fileExtension,
        content: value,
      };
      setPreviewContent(filecontent);
      await new Promise((resolve) => setTimeout(resolve, 0));
      // handleClick({});
    } catch (error) {
      setPreviewContent({
        name: "code",
        url: "",
        fileType: fileExtension,
        content: "// Failed to load file content",
      });
    }
  };

  const getStatusText = () => {
    switch (copyStatus) {
      case "copying":
        return "Copying...";
      case "copied":
        return "Copied!";
      case "error":
        return "Failed to copy";
      default:
        return "Copy";
    }
  };

  return (
    <div className="code-block-container relative rounded-md overflow-hidden">
      {/* Top bar */}
      <div className="code-block-header flex items-center justify-between px-4 py-2 bg-gray-800 text-gray-300 text-xs border-b border-gray-700">
        <div className="font-mono">{language || "code"}</div>
        <div className="flex space-x-2">
          <button
            onClick={copyToClipboard}
            className="text-gray-300 hover:text-white transition-colors focus:outline-none flex items-center space-x-1"
            aria-label="Copy code to clipboard"
          >
            <span>{getStatusText()}</span>
            {copyStatus === "copied" && (
              // <CheckIcon className="text-green-500" />
              <CheckIcon />
            )}
            {copyStatus === "idle" && <CopyIcon />}
          </button>
          <button
            onClick={downloadCode}
            className="text-gray-300 hover:text-white transition-colors focus:outline-none flex items-center space-x-1"
            aria-label="Download code"
          >
            <span>Download</span>
            <DownloadIcon />
          </button>

          { amsUser && language !== "txt" && showEditButtonProp && (
            <button
              onClick={editCode}
              className="text-gray-300 hover:text-white transition-colors focus:outline-none flex items-center space-x-1"
              aria-label="Edit code"
            >
              <span>Edit</span>
              <EditIcon />
            </button>
          )}
        </div>
      </div>

      {/* Code content */}
      <Highlight
        theme={themes.okaidia}
        code={String(value).replace(/\n$/, "")}
        language={language || "json"}
      >
        {({ className, style, tokens, getLineProps, getTokenProps }) => (
          <pre
            className={`${className} p-4 m-0 text-break code-pre`}
            style={{ ...style }}
          >
            {tokens.map((line, i) => (
              <div key={i} {...getLineProps({ line })}>
                {line.map((token, key) => (
                  <span key={key} {...getTokenProps({ token })} />
                ))}
              </div>
            ))}
          </pre>
        )}
      </Highlight>
    </div>
  );
};

interface MarkdownProps {
  content: any;
  containerWidth?: number;
  token?: string;
  conversationId?: string;
  showMore?: boolean;
  showEditButton?: boolean;
}

function extractFileBlocks(content: string) {
  const fileRegex = /File upload (\S+) ({.*?})/g;
  const matches: { hash: string; name: string; type: string; conversationId?: string }[] = [];

  let match: RegExpExecArray | [any, any, any] | null;
  while ((match = fileRegex.exec(content)) !== null) {
    const [, hash, json] = match;
    try {
      const meta = JSON.parse(json);
      matches.push({ hash, ...meta });
    } catch {
      // skip invalid JSON
    }
  }

  return matches;
}

// Helper function to normalize indentation and strip markdown code blocks
const normalizeIndentation = (content: string): string => {
  if (!content || typeof content !== 'string') return content;
  
  // First strip ```markdown blocks
  const stripMarkdownBlocks = (text: string): string => {
    // This regex pattern matches ```markdown ... ``` blocks, being flexible with whitespace
    // It captures the content inside the block to preserve it without the backticks
    const markdownBlockRegex = /```\s*(markdown|md)\s*\n([\s\S]*?)\n\s*```/g;
    
    return text.replace(markdownBlockRegex, (_, __, blockContent) => {
      // Return just the content without the backticks and language marker
      return blockContent;
    });
  };
  
  // Apply markdown block stripping
  const withoutMarkdownBlocks = stripMarkdownBlocks(content);
  
  // Split the content into lines
  const lines = withoutMarkdownBlocks.split('\n');
  
  // Process each line
  const normalizedLines = lines.map(line => {
    // Check if line starts with exactly 4 spaces (but not more)
    if (/^    [^ ]/.test(line)) {
      // Replace 4 spaces with 2 spaces to prevent code block interpretation
      return '  ' + line.substring(4);
    }
    return line;
  });
  
  // Join the lines back together
  return normalizedLines.join('\n');
};

export default function CustomMarkdown({
  content,
  containerWidth,
  token,
  conversationId,
  showMore = false,
  showEditButton = true
}: MarkdownProps) {
  /**
   * Capture situations where the codeblock tries to bleed over the screen's boundary
   * Lets peg this to the width of the parent container of the Messages Component (Index.tsx)
   */
  let customMaxWidth = containerWidth ? `${containerWidth.toString()}px` : "";
  
  // Normalize the indentation in the content
  const normalizedContent = normalizeIndentation(content);


  // Escape HTML tags in the content to prevent rendering, but allow <br> tags
  function escapeHtmlAllowBr(html: string) {
    // Replace <br>, <br/>, <br /> with a placeholder
    const BR_PLACEHOLDER = '___BR_PLACEHOLDER___';
    let safe = html.replace(/<br\s*\/?\s*>/gi, BR_PLACEHOLDER);
    // Escape all other HTML
    safe = safe.replace(/</g, "&lt;").replace(/>/g, "&gt;");
    // Restore <br />
    safe = safe.replace(new RegExp(BR_PLACEHOLDER, 'g'), '<br />');
    return safe;
  }

  // Only escape if content is a string
  const safeContent = typeof normalizedContent === 'string' ? escapeHtmlAllowBr(normalizedContent) : normalizedContent;

  // Refs and state for show more/less functionality
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(0);
  const [expanded, setExpanded] = useState(!showMore);
  const MAX_HEIGHT = 300; // Maximum height in pixels before collapse

  const fileBlocks = extractFileBlocks(normalizedContent);
  const [loadedImages, setLoadedImages] = useState({});
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});
  const { setPreviewContent } = usePreview();
  const { apptheme } = useAppTheme();
  const { checkAuthToken } = useContext(AuthContext);

  const [imageStatus, setImageStatus] = useState<
    Record<string, "loading" | "loaded" | "error">
  >({});
  
  // First useEffect for content height
  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [content]);
  
  const toggleExpand = () => {
    const wasExpanded = expanded;
    
    // Toggle the expanded state
    setExpanded(!expanded);
  };

  const handleLoad = (hash: string) => {
    setImageStatus((prev) => ({ ...prev, [hash]: "loaded" }));
  };

  const handleError = (hash: string) => {
    setImageStatus((prev) => ({ ...prev, [hash]: "error" }));
  };

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    const link = document.createElement("a");
    link.href = content?.url || "";
    link.download = content?.name || "";
    link.click();
  };

  const handleClick = async ({ fileLink }: HandleClickProps) => {
    console.log(`handleClick: ${JSON.stringify(content)}`);

    if (isFileContent(content)) {
      const isImageFile = content.fileType?.startsWith("image/");
      const isExcelFile =
        content.fileType?.includes("sheet") ||
        /\.(xls|xlsx|csv)$/i.test(content.name);

      const isTextOrCode =
        content.fileType?.startsWith("text/") ||
        /\.(js|ts|json|py|html|css|md|txt|code)$/i.test(content.name);

      if (isImageFile) {
        setPreviewContent({
          name: content.name,
          url: content.url,
          fileType: content.fileType,
          content: undefined, // images don't need text
        });
      } else if (isExcelFile) {
        try {
          const response = await fetch(content.url);
          const arrayBuffer = await response.arrayBuffer();
          const workbook = read(arrayBuffer, { type: "array" });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const html = utils.sheet_to_html(worksheet);

          setPreviewContent({
            name: content.name,
            url: content.url,
            fileType: content.fileType,
            content: html,
          });
        } catch (error) {
          logError("Error parsing Excel file", error);
          setPreviewContent({
            name: content.name,
            url: content.url,
            fileType: content.fileType,
            content: "<p>Failed to load Excel content</p>",
          });
        }
      } else if (isTextOrCode) {
        try {
          const response = await fetch(content.url);
          const text = await response.text();

          setPreviewContent({
            name: content.name,
            url: content.url,
            fileType: content.fileType,
            content: text,
          });
        } catch (error) {
          setPreviewContent({
            name: content.name,
            url: content.url,
            fileType: content.fileType,
            content: "// Failed to load file content",
          });
        }
      } else {
        // For unsupported files, clear preview
        setPreviewContent(null);
      }
    } else {
      console.log("2");
      setPreviewContent({
        name: fileLink?.split("/").pop() || "unknown file",
        url: fileLink || "",
        fileType: fileLink?.split(".").pop() || "unknown file",
        content: undefined,
      });
    }
  };

  const handleLinkClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    href: string
  ) => {
    e.preventDefault();

    // Check if it's an external link
    if (href.startsWith("http")) {
      // Show confirmation dialog for external links
      if (
        window.confirm(
          "You are about to visit an external website. Do you want to continue?"
        )
      ) {
        window.open(href, "_blank", "noopener,noreferrer");
      }
    } else {
      // Handle internal links here if needed
      window.location.href = href;
    }
  };

  const getFileIcon = (ext: string) => {
    switch (ext.toLowerCase()) {
      case "pdf":
        return <IconFileTypePdf size={20} className="text-red-500 mr-2" />;
      case "doc":
      case "docx":
        return <IconFileWord size={20} className="text-blue-600 mr-2" />;
      case "xls":
      case "xlsx":
      case "xlsb":
        return <IconFileTypeXls size={20} className="text-green-600 mr-2" />;
      case "ppt":
      case "pptx":
        return <IconFileTypePpt size={20} className="text-orange-500 mr-2" />;
      case "zip":
      case "rar":
        return <IconFileZip size={20} className="text-yellow-500 mr-2" />;
      case "txt":
        return <IconFileText size={20} className="text-gray-600 mr-2" />;
      case "js":
      case "ts":
      case "html":
      case "css":
      case "json":
        return <IconFileCode size={20} className="text-purple-500 mr-2" />;
      default:
        return <IconFile size={20} className="text-gray-500 mr-2" />;
    }
  };

  useEffect(() => {
    if (fileBlocks.length > 0) {
      const preload = async () => {
        const newImages = {};
        const signedUrls = {};
        
        // Fetch signed URLs for each file
        await Promise.all(
          fileBlocks.map(async (fileMeta) => {
            try {
              // Create a file path from the hash and file name - must match backend path structure
              const filePath = `hash/${fileMeta.hash}/file/${fileMeta.name}`;
              
              // Get a signed URL for the file
              const { signedUrl } = await getSignedUrl(token || '', filePath, conversationId || '');
              signedUrls[fileMeta.hash] = signedUrl;
              
              // If it's an image, preload it
              if (fileMeta.type.startsWith("image/")) {
                const img = new Image();
                img.src = signedUrl;
                return new Promise((resolve) => {
                  img.onload = () => {
                    newImages[fileMeta.hash] = true;
                    resolve(true);
                  };
                  img.onerror = resolve;
                });
              }
            } catch (error) {
              logError(`Error fetching signed URL for ${fileMeta.name}`, error);
            }
            return Promise.resolve();
          })
        );
        
        setLoadedImages(newImages);
        // Store the signed URLs in state
        setSignedUrls(signedUrls);
      };
      preload();
    }
  }, [normalizedContent]);

  if (fileBlocks.length > 0) {
    return (
      <div className="">
        {fileBlocks.map((fileMeta, idx) => {
          const isImage = fileMeta.type.startsWith("image/");
          // Use the signed URL if available, otherwise construct a placeholder URL
          const fileUrl = signedUrls[fileMeta.hash] || `#${fileMeta.hash}`;
          const safeFileUrl = safeUrl(fileUrl);

          return isImage ? (
            imageStatus[fileMeta.hash] === "loaded" ? (
              <img
                key={`${fileMeta.hash}-${idx}`}
                src={safeFileUrl}
                alt={fileMeta.name}
                onLoad={() => handleLoad(fileMeta.hash)}
                onError={() => handleError(fileMeta.hash)}
                className="rounded-md max-w-[300px] object-contain border border-gray-300"
              />
            ) : imageStatus[fileMeta.hash] === "error" ? (
              <div
                key={`${fileMeta.hash}-${idx}`}
                className="w-[300px] h-[200px] bg-red-50 border border-red-300 rounded flex items-center justify-center text-sm text-red-500 flex-col"
              >
                <IconAlertTriangle size={32} className="mb-1" />
                Image failed to load
              </div>
            ) : (
              <div
                key={`${fileMeta.hash}-${idx}`}
                className="w-[300px] h-[200px] bg-gray-100 border border-gray-300 flex items-center justify-center text-sm text-black flex-col font-mono"
              >
                <IconPhotoX size={32} className="mb-1 text-red-600" />
                <span className="text-black">Loading image...</span>
              </div>
            )
          ) : (
            <div
              key={`${fileMeta.hash}-${idx}`}
              onClick={(e) => {
                e.preventDefault();
                if (signedUrls[fileMeta.hash]) {
                  handleClick({ fileLink: safeFileUrl });
                } else {
                  const filePath = `hash/${fileMeta.hash}/file/${fileMeta.name}`;
                  getSignedUrl(token || '', filePath, conversationId || '')
                    .then(({ signedUrl }) => {
                      setSignedUrls(prev => ({ ...prev, [fileMeta.hash]: signedUrl }));
                      handleClick({ fileLink: safeUrl(signedUrl) });
                    })
                    .catch(error => {
                      logError(`Error fetching signed URL for ${fileMeta.name}`, error);
                      showToast(`Failed to load file: ${fileMeta.name}`, "error");
                    });
                }
              }}
              className={`inline-flex items-center px-3 py-2 text-sm font-medium cursor-pointer rounded shadow-sm`}
            >
              {getFileIcon(fileMeta.name.split(".").pop() || "")}
              <span className="truncate max-w-[200px]">{fileMeta.name}</span>
              <IconDownload
                onClick={(e) => {
                  e.stopPropagation();
                  if (signedUrls[fileMeta.hash]) {
                    const link = document.createElement("a");
                    link.href = safeFileUrl;
                    link.download = fileMeta.name;
                    link.click();
                  } else {
                    const filePath = `hash/${fileMeta.hash}/file/${fileMeta.name}`;
                    getSignedUrl(token || '', filePath, conversationId || '')
                      .then(({ signedUrl }) => {
                        setSignedUrls(prev => ({ ...prev, [fileMeta.hash]: signedUrl }));
                        const link = document.createElement("a");
                        link.href = safeUrl(signedUrl);
                        link.download = fileMeta.name;
                        link.click();
                      })
                      .catch(error => {
                        logError(`Error fetching signed URL for ${fileMeta.name}`, error);
                        showToast(`Failed to download file: ${fileMeta.name}`, "error");
                      });
                  }
                }}
                className="ml-3 w-4 h-4 hover:text-primary"
              />
            </div>
          );
        })}
      </div>
    );
  }

  function isFileContent(
    content: MessageContent
  ): content is { type: "file"; name: string; url: string; fileType: string } {
    return (
      typeof content === "object" &&
      content !== null &&
      "type" in content &&
      content.type === "file"
    );
  }

  // Add this after the isFileContent function
  function isExcelJson(content: string): boolean {
    try {
      // Check for content wrapped in excel tags
      const excelRegex = /<excel>(.*?)<\/excel>/;
      const match = content.match(excelRegex);

      if (!match) return false;

      // Verify it's an array or object
      return true;
    } catch {
      return false;
    }
  }

  function extractExcelContent(content: string): { excelContent: string, beforeContent: string, afterContent: string } {
    try {
      // Check for content wrapped in excel tags
      const excelRegex = /<excel>(.*?)<\/excel>/;
      const match = content.match(excelRegex);

      if (!match) return { excelContent: "", beforeContent: content, afterContent: "" };

      // Get the full match including tags
      const fullMatch = match[0];
      // Get the position where the match starts and ends
      const startPos = content.indexOf(fullMatch);
      const endPos = startPos + fullMatch.length;
      
      // Extract content before and after the Excel block
      const beforeContent = content.substring(0, startPos);
      const afterContent = content.substring(endPos);
      // Extract just the Excel content with tags
      const excelContent = fullMatch;

      return { excelContent, beforeContent, afterContent };
    } catch {
      return { excelContent: "", beforeContent: content, afterContent: "" };
    }
  }

  interface HandleClickProps {
    fileLink?: string;
  }

  // Fallback UI component
  const FallbackImage = () => (
    <div className="w-[300px] h-[200px] bg-red-50 border border-red-300 rounded flex items-center justify-center text-sm text-red-500 flex-col">
      <IconAlertTriangle size={32} className="mb-1" />
      Image failed to load
    </div>
  );

  // Custom image component with error handling and URL validation
  const CustomImage = ({ src, alt }: any) => {
    const [isError, setIsError] = useState(false);
    // SECURITY: Validate image src
    if (!isTrustedUrl(src)) {
      return <FallbackImage />;
    }
    return (
      <div>
        {!isError ? (
          <img
            onClick={() => handleClick({ fileLink: src })}
            src={src}
            alt={escapeAttr(alt)}
            onError={() => setIsError(true)}
            style={{ display: isError ? "none" : "block" }}
          />
        ) : (
          <FallbackImage />
        )}
      </div>
    );
  };

  let filebg =
    apptheme.name === "dark-theme"
      ? "bg-gray-800 text-white"
      : "bg-[#E6E6FA] text-zinc-900";

  // Create common markdown components to avoid duplication
  const getMarkdownComponents = (showEditButton) => ({
    code: ({ className, children, ...props }) => {
      const match = /language-(\w+)/.exec(className || "");
      const language = match ? match[1] : null;

      // Skip special rendering only for markdown language
      if (language === "markdown" || language === "md") {
        return (
          <>{children}</>
        );
      }

      // For all other languages, render as normal
      if (match) {
        return (
          <CopyButtonCodeBlock
            value={String(children).replace(/\n$/, "")}
            language={language}
            showEditButton={showEditButton}
          />
        );
      } else {
        if (String(children).length > 150) {
          return (
            <CopyButtonCodeBlock
              value={String(children)}
              language={"text"}
              showEditButton={showEditButton}
            />
          );
        } else {
          return (
            <code className={`${className}`} {...props}>
              {children}
            </code>
          );
        }
      }
    },
    img: ({ node, ...props }) => <CustomImage {...props} />,
    a: ({ node, children, href, ...props }) => {
      const isExternal = href?.startsWith("http");

      return (
        <a
          href={href}
          onClick={(e) => handleLinkClick(e, href || "")}
          {...props}
          className={`inline-flex items-center ${
            isExternal ? "external-link" : ""
          }`}
          rel={isExternal ? "noopener noreferrer" : undefined}
          aria-label={
            isExternal ? `${children} (opens in new tab)` : undefined
          }
        >
          {children}
        </a>
      );
    },
  });

  console.log(`Markdown content: ${JSON.stringify(content)}`);


  return (
    <MarkdownContainer>
      <div
        ref={contentRef}
        className="text-sm font-normal leading-loose content-transition"
        // style={{
        //   maxWidth: customMaxWidth,
        //   maxHeight: !expanded && showMore && contentHeight > MAX_HEIGHT ? `${MAX_HEIGHT}px` : `${contentHeight}px`,
        //   overflow: !expanded && showMore && contentHeight > MAX_HEIGHT ? 'hidden' : undefined,
        //   position: 'relative'
        // }}
      >
        {isExcelJson(content) ? (
          <>
            {(() => {
              const { excelContent, beforeContent, afterContent } = extractExcelContent(content);
              const safeBeforeContent = escapeHtmlAllowBr(beforeContent);
              const safeAfterContent = escapeHtmlAllowBr(afterContent);

              return (
                <>
                  {/* Render before content if it exists */}
                  {beforeContent.trim() && (
                    <Markdown
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[rehypeRaw]}
                      components={getMarkdownComponents(showEditButton)}
                    >
                      {safeBeforeContent}
                    </Markdown>
                  )}

                  {/* Render Excel widget */}
                  <div className="my-2">
                    <div
                      onClick={async (e) => {
                        e.preventDefault();
                        const response = await getWorkbookfromJson(excelContent);
                        if (!response) {
                          showToast(`Error parsing data`, "error");
                        }
                      }}
                      className={`inline-flex items-center px-3 py-2 text-sm font-medium cursor-pointer rounded shadow-sm ${filebg}`}
                    >
                      {getFileIcon("xlsx")}
                      <span className="truncate max-w-[200px]">Excel File</span>
                      <IconDownload className="ml-3 w-4 h-4 hover:text-primary" />
                    </div>
                  </div>

                  {/* Render after content if it exists */}
                  {afterContent.trim() && (
                    <Markdown
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[rehypeRaw]}
                      components={getMarkdownComponents(showEditButton)}
                    >
                      {safeAfterContent}
                    </Markdown>
                  )}
                </>
              );
            })()}
          </>
        ) : isFileContent(content) === true ? (
          content.fileType.startsWith("image/") ? (
            <img
              src={content.url}
              alt={content.name}
              className=" max-w-[200px] cursor-pointer"
              onClick={() => handleClick({})}
            />
          ) : (
            <div
              onClick={(e) => {
                e.preventDefault();
                handleClick({});
              }}
              className={`inline-flex items-center px-3 py-2 text-sm font-medium cursor-pointer rounded shadow-sm ${filebg}`}
            >
              {getFileIcon(content.name.split(".").pop() || "")}
              <span className="truncate max-w-[200px]">{content.name}</span>
              <IconDownload
                onClick={handleDownload}
                className="ml-3 w-4 h-4 hover:text-primary"
              />
            </div>
          )
        ) : (
          <Markdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={getMarkdownComponents(showEditButton)}
          >
            {safeContent}
          </Markdown>
        )}
      </div>
      {showMore && contentHeight > MAX_HEIGHT && (
        <div className="w-full flex justify-center mt-2 mb-1">
          <button
            onClick={toggleExpand}
            className="show-more-button"
            aria-expanded={expanded}
          >
            {expanded ? 'Show Less' : 'Show More'}
            <svg 
              className={`ml-1 w-3 h-3 ${expanded ? 'transform rotate-180' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24" 
              xmlns="http://www.w3.org/2000/svg"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
            </svg>
          </button>
        </div>
      )}
    </MarkdownContainer>
  );
}

// SECURITY: Only allow https:// and trusted domains for images/files
const isTrustedUrl = (url: string) => {
  try {
    const u = new URL(url, window.location.origin);
    // Allow only https and trusted CDN
    return (
      u.protocol === 'https:' && [
        'cdn.yourapp.com',
        window.location.hostname // allow same-origin
      ].includes(u.hostname)
    );
  } catch {
    return false;
  }
};

// SECURITY: Escape file/image names to prevent XSS in attributes
const escapeAttr = (str: string) => String(str)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

// ||10/12/2025-The code exposes risk by rendering unvalidated fileUrl, which may allow XSS, malicious content loading, or leakage of internal identifiers. Always validate protocols (https, blob) and whitelist trusted domains before using URLs. Apply recursive sanitization and centralized logging to prevent sensitive data exposure. Enforce Content Security Policy (CSP) and avoid storing or exposing raw secrets or hashes in payloads.
// SECURITY: Safe URL validation for file/image rendering
const safeUrl = (url: string) => {
  try {
    const u = new URL(url, window.location.origin);
    // Allow only https and blob protocols, and trusted domains
    if (["https:", "blob:"].includes(u.protocol) && [
      "cdn.yourapp.com",
      window.location.hostname // allow same-origin
    ].includes(u.hostname)) {
      return url;
    }
    // Log blocked attempt (centralized logging can be added here)
    console.warn("Blocked unsafe fileUrl:", url);
    return "/fallback.png";
  } catch (e) {
    // Log error
    console.warn("Invalid fileUrl:", url, e);
    return "/fallback.png";
  }
};